/**
 * Générateur — Additionner, soustraire, multiplier et diviser pour résoudre
 * des problèmes et contrôler la vraisemblance de son résultat.
 * Attendu ID : 899 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 1)
 *
 * Plan de cartes (validé par le responsable pédagogique, revu le 2026-08-01) :
 *
 *   Carte « voyages » — Ex.5 Les livres du CDI                    [calculatrice]
 *     Formats : texte_trou_saisie, texte_trou_drag, qcm_unique (rotation
 *     voyages / dernier voyage).
 *     Construction : b ∈ [9..25], q ∈ [12..40], r ∈ [2..b−1] (r ≥ 2 : dernier
 *     voyage incomplet garanti ET pluriel « {{r}} livres » toujours correct),
 *     a = b×q + r. Quotient, reste, voyages (q+1) et charge du dernier voyage
 *     (r) uniques par construction.
 *     La consigne de la SAISIE rappelle « le reste doit être plus petit que b » :
 *     l'égalité a = b × □ + □ admet des dizaines de couples vrais, un seul est
 *     la division euclidienne.
 *     Le DRAG n'a que les deux premières lignes, et re-tire tant que r ∈ {q, q+1} :
 *     le pool d'étiquettes est [mots + distracteurs] SANS déduplication
 *     (TexteTrouEleve.jsx), toute valeur répétée y apparaîtrait en double et
 *     désignerait le reste sans qu'aucune division soit faite.
 *     Misconceptions drag : q−1, q+2, b−r, b, r+2, q+3.
 *     Misconceptions QCM  : question a → q, q+2, r ; question b → b−r, b, r−1,
 *     TOUS ≤ b (un choix au-dessus de la capacité s'élimine sans diviser).
 *
 *   Carte « deux_etapes » — Ex.20 Le lecteur DVD                   [calculatrice]
 *     Formats : texte_trou_saisie, qcm_unique, texte (saisie).
 *     Construction : tout en CENTIMES (entiers, zéro flottant). L = prix
 *     appareil, n ∈ {2,3,4} articles à p centimes, T = L + n×p. Les deux
 *     étapes (T−L puis ÷n) tombent juste par construction. Affichage via
 *     fmtC (virgule FR, zéros finaux retirés — convention des gens 6e).
 *     Misconceptions : T÷n (oublie l'appareil), T−L (s'arrête à l'étape 1),
 *     L÷n (mauvaise donnée) — chacune retenue seulement si elle s'écarte d'au
 *     moins 50 centimes de la bonne réponse ; L/n et p se croisent, un choix à
 *     un centime près départagerait l'attention et non le raisonnement.
 *
 *   Carte « haies » — Ex.13 Le 110 m haies                         [calculatrice]
 *     Formats : texte_trou_saisie, qcm_unique (rotation à TROIS variantes).
 *     Construction : course fixe 110 m (réalisme athlétisme), n = 10 haies,
 *     tout en CENTIMÈTRES : d1 ∈ [1250..1450], e ∈ [850..950] →
 *     position = d1 + 9e ∈ [8900..10000] → arrivée = 11000 − pos ∈
 *     [1000..2100] > 0 par bornes.
 *     L'énoncé DÉFINIT l'épreuve (« une course de 110 m ») : le 110 du calcul
 *     n'apparaissait nulle part ailleurs que dans un nom d'épreuve.
 *     Le nombre d'intervalles est un TROU ({{9}}) : donné en clair, il livrait
 *     le seul obstacle du problème et ne laissait qu'une exécution.
 *     La donnée inutile est une phrase autonome, servie UNIQUEMENT au QCM
 *     (`enonceHaies(t, avecDonneeInutile)`) : jamais interrogée dans le texte à
 *     trous, elle n'y était que du bruit. Elle TOURNE (hauteur / couloirs /
 *     dossard) — exactement une par énoncé.
 *     QCM en trois variantes : ① distance à l'arrivée (numérique) ; ② « quelle
 *     donnée est inutile ? » cible arrivée → réponse = le parasite ; ③ même
 *     question cible POSITION de la dernière haie, énoncé sans parasite →
 *     réponse = la longueur de la course. Sans ③, les trois distracteurs
 *     étaient invariants et la réponse toujours le libellé parasite.
 *     Misconceptions numériques : une seule haie comptée, 10 espacements au
 *     lieu de 9, position confondue avec distance restante.
 *
 *   Carte « vraisemblance » — Ex.23 L'achat de Kamel        [PAS de calculatrice]
 *     Formats : qcm_unique (verdict + justification), tableau_vf.
 *     Trancher par ordre de grandeur EST l'attendu : un total exact
 *     supprimerait l'exercice.
 *     Construction : centimes ; prix frôlant les entiers (delta ∈ ±[1..5] c).
 *     Deux régimes : « clair » (somme des entiers ≠ B, verdict par ordre de
 *     grandeur) et « limite » (somme des entiers = B, deltas TOUS de même
 *     signe → verdict décidé par « un peu plus / un peu moins »). Le V/F
 *     inclut une paire d'affirmations de vérités opposées par construction
 *     (jamais tout-vrai / tout-faux).
 *     Les distracteurs du QCM ont une LONGUEUR comparable à la bonne réponse,
 *     et l'un d'eux porte le BON verdict avec un raisonnement partiel : sinon
 *     « prends la plus longue » ou « cherche le seul Oui » suffisait à réussir.
 *
 *   Carte « perimetre » (hors quota) — Ex.3         [PAS de calculatrice]
 *     Formats : texte (saisie), qcm_unique. Figure SVG à l'échelle
 *     (elements_additionnels image) : triangle ABC, côtés AB et CB étiquetés,
 *     « ? » sur AC. AC = P − AB − CB unique par construction ; inégalité
 *     triangulaire garantie par tirage borné + repli déterministe (l'exercice
 *     source : AB=4, CB=6, AC=7, P=17 — jamais atteint en pratique).
 *     Misconceptions : P−AB (oublie un côté), AB+CB (somme au lieu de
 *     différence), P−CB, |AB−CB| (soustrait les deux données entre elles),
 *     AC−1. Les trois premières sont des SOMMES DE DEUX CÔTÉS, donc toutes
 *     supérieures à AC : servies seules, elles faisaient de la bonne réponse
 *     la plus petite des quatre à tous les coups.
 *     L'espace de tirage ne compte que 180 triangles (16 périmètres) : le
 *     harnais y plafonne vers 33-38/40, c'est structurel et assumé — deux ou
 *     trois exercices par élève ne se répètent jamais en pratique.
 *
 * Conventions transverses (revue 2026-08-01) :
 *   - Espace INSÉCABLE entre chaque nombre et son unité (`NB`) : « 12,5 € »
 *     ne doit pas se couper en fin de ligne, et au bord d'un trou le NBSP
 *     déclenche le groupe whitespace-nowrap de TexteTrouEleve.
 *   - Aucune consigne « écris les nombres sans zéro inutile » : la tolérance
 *     numérique est active en saisie (cf. toleranceNumerique), « 12,50 » est
 *     déjà accepté — l'exiger promettait une sévérité inexistante.
 *   - Le flag `calculatrice` vit dans `contenu` (seul canal qui survit au POST),
 *     posé format par format via avecCalculatrice().
 *
 * Adaptations d'énoncés (doc cluster → en ligne) :
 *   - Ex.5 : les questions a/b deviennent un texte à trous progressif
 *     (« 390 = 14 × .. + .. ; .. voyages ; .. livres ») et un QCM en rotation.
 *   - Ex.13 : la donnée inutile devient une question QCM fermée, à deux cibles.
 *   - Ex.23 : « dis si… » devient QCM verdict+justification et tableau V/F.
 *   - Ex.3 : figure support en SVG, seule la zone de réponse est fermée.
 *
 * Coups de pouce : src/generators/coupsDePouce/899.js — 5 fiches (une par
 * carte), 10 figures SVG animées en SMIL, figées en fin d'animation et
 * rejouables. Valeurs d'exemples toutes HORS des plages tirées ici.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 899;

const L_VOY   = 'Résoudre un problème de voyages (division euclidienne)';
const L_2ET   = "Résoudre un problème à deux étapes (prix d'un article)";
const L_HAIES = 'Résoudre un problème multi-étapes (course de haies)';
const L_VRAI  = "Contrôler la vraisemblance d'un résultat";
const L_PERIM = 'Trouver un côté connaissant le périmètre (figure)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centimes (entier) → écriture décimale FR sans zéros finaux ("2490" → "24,9"). */
const fmtC = (centimes) => {
    const euros = Math.floor(centimes / 100);
    const cents = centimes % 100;
    if (cents === 0) return String(euros);
    const dec = String(cents).padStart(2, '0').replace(/0+$/, '');
    return `${euros},${dec}`;
};

/** Élément image d'énoncé — convention §12 : imageUrl en data-URI + maxWidth. */
const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

/**
 * Déduplique des choix de QCM par valeur normalisée (virgule ≡ point).
 * Les candidats (misconceptions) sont MÉLANGÉS avant sélection → rotation des
 * familles de pièges (§0.3) ; les compléments restent un repli ordonné.
 */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Voyages (division euclidienne)
// ═══════════════════════════════════════════════════════════

const CTX_VOYAGES = [
    { qui: 'La documentaliste du CDI', pronom: 'Elle', objets: 'livres', mission: 'les ranger sur des étagères' },
    { qui: 'Le cuisinier de la cantine', pronom: 'Il', objets: 'bouteilles de jus', mission: 'les stocker dans la réserve' },
    { qui: 'La gardienne du gymnase', pronom: 'Elle', objets: 'chaises', mission: 'les installer dans la salle' },
    { qui: 'Le fleuriste du quartier', pronom: 'Il', objets: 'pots de fleurs', mission: 'les disposer en vitrine' },
];

/** Tire la situation de division euclidienne (a = b×q + r, 2 ≤ r < b).
 *  r ≥ 2 : le dernier voyage incomplet existe ET « {{r}} livres » reste au
 *  pluriel sur tous les tirages (défaut juge : « 1 livres » quand r = 1). */
const tirerVoyages = () => {
    const b = randInt(9, 25);
    const q = randInt(12, 40);
    const r = randInt(2, b - 1);
    const a = b * q + r;
    const ctx = randomChoice(CTX_VOYAGES);
    return { a, b, q, r, ctx };
};

const enonceVoyages = ({ a, b, ctx }) =>
    `<p>${ctx.qui} reçoit <strong>${a} ${ctx.objets}</strong> et doit ${ctx.mission}. ` +
    `${ctx.pronom} ne peut en transporter que <strong>${b}</strong> à la fois.</p>`;

const phraseVoyages = ({ a, b, q, r, ctx }) =>
    `${a} = ${b} × {{${q}}} + {{${r}}}\n` +
    `${ctx.pronom} devra faire {{${q + 1}}} voyages.\n` +
    `Au dernier voyage, ${ctx.pronom.toLowerCase()} transportera {{${r}}} ${ctx.objets}.`;

const genVoyagesTrouSaisie = () => {
    const t = tirerVoyages();
    // Rappel de la condition du reste (revue 2026-08-01) : l'égalité a = b × □ + □
    // admet des dizaines de couples vrais, un seul est la division euclidienne.
    const texte = enonceVoyages(t) +
        `<p>Complète le raisonnement (le reste doit être plus petit que ${t.b}) :</p>`;
    const payload = makeTexteTrou(phraseVoyages(t), texte, { ...opts(L_VOY), mode: 'saisie' });
    // Calculatrice AUTORISÉE (revue 2026-08-01) : division euclidienne à deux
    // chiffres, l'enjeu est l'interprétation du quotient et du reste, pas la potence.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

/**
 * Variante DRAG : les deux premières lignes seulement. Le pool d'étiquettes est
 * [mots des trous + distracteurs] SANS déduplication (TexteTrouEleve.jsx) —
 * répéter {{r}} y servirait le reste en deux exemplaires, seule valeur en
 * double, ce qui le désigne sans qu'aucune division soit faite (revue
 * 2026-08-01). La ligne du dernier voyage reste portée par le format saisie.
 */
const phraseVoyagesDrag = (t) => phraseVoyages(t).split('\n').slice(0, 2).join('\n');

const genVoyagesTrouDrag = () => {
    let t = tirerVoyages();
    // r = q ou r = q+1 : deux trous attendraient la même valeur, servie en deux
    // étiquettes identiques — la paire visible désigne le reste sans division.
    while (t.r === t.q || t.r === t.q + 1) t = tirerVoyages();
    const phrase = phraseVoyagesDrag(t);
    // Misconceptions : q−1 / q+2 (mauvais compte de voyages), b−r (complément
    // du reste), b (croit le dernier voyage plein), r+2 (reste décalé),
    // q+3 (quotient plausible). 6 candidats mélangés → ≥ 3 survivent au filtre
    // sur tout tirage (au plus 3 collisions simultanées possibles) + rotation.
    const candidats = shuffle([t.q - 1, t.q + 2, t.b - t.r, t.b, t.r + 2, t.q + 3].map(String));
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const texte = enonceVoyages(t) + '<p>Complète le raisonnement avec les bonnes étiquettes :</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_VOY), mode: 'drag', distracteurs });
    // Calculatrice AUTORISÉE (revue 2026-08-01) : même division que la saisie,
    // elle sert ici à vérifier avant de choisir une étiquette.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

/** Calculatrice AUTORISÉE (revue 2026-08-01) sur toute la carte « voyages » :
 *  la compétence évaluée est l'interprétation du quotient et du reste, pas la
 *  technique de la potence. Le flag vit dans `contenu` — seul canal qui survit
 *  au POST (`_meta` est strippé). */
const avecCalculatrice = (payload) => {
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

const genVoyagesQcm = () => {
    const t = tirerVoyages();
    const { b, q, r, ctx } = t;
    if (randomChoice([true, false])) {
        // Question a — nombre minimal de voyages (bonne réponse : q+1)
        const bonne = String(q + 1);
        const distracteurs = dedupeChoix(bonne, [q, q + 2, r].map(String), [q + 3, q - 1].map(String));
        const texte = enonceVoyages(t) +
            `<p>Combien de voyages devra-t-${ctx.pronom === 'Elle' ? 'elle' : 'il'} faire au minimum ?</p>`;
        return avecCalculatrice(makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_VOY)));
    }
    // Question b — objets transportés au dernier voyage (bonne réponse : r)
    const bonne = String(r);
    // Tous les candidats sont ≤ b : on ne peut pas transporter plus que la
    // capacité, un choix au-dessus s'élimine sans diviser (revue 2026-08-01 :
    // le quotient q, ancien candidat, dépassait b dans 80 % des tirages).
    // r ∈ [2, b−1] garantit b−r ≥ 1, r−1 ≥ 1, r+1 ≤ b, b−1 ≥ 8.
    const distracteurs = dedupeChoix(bonne, [b - r, b, r - 1].map(String), [b - 1, r + 1].map(String));
    const texte = enonceVoyages(t) +
        `<p>Combien de ${ctx.objets} transportera-t-${ctx.pronom === 'Elle' ? 'elle' : 'il'} au dernier voyage ?</p>`;
    return avecCalculatrice(makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_VOY)));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Problème à deux étapes (appareil + n articles)
// ═══════════════════════════════════════════════════════════

const PRENOMS = ['Alice', 'Kamel', 'Inès', 'Lucas', 'Maya', 'Nolan', 'Sofia', 'Rayan'];

const CTX_2ETAPES = [
    { appareil: 'un lecteur DVD', def: 'Le lecteur DVD', article: 'DVD', articles: 'DVD', unArticle: 'un DVD' },
    // Manette et non console (revue 2026-08-01) : l'appareil est tiré entre
    // 29 € et 80 €, plage juste pour une manette, absurde pour une console.
    { appareil: 'une manette de jeu', def: 'La manette', article: 'jeu vidéo', articles: 'jeux vidéo', unArticle: 'un jeu vidéo' },
    { appareil: 'une imprimante', def: "L'imprimante", article: "cartouche d'encre", articles: "cartouches d'encre", unArticle: "une cartouche d'encre" },
    { appareil: 'une cafetière', def: 'La cafetière', article: 'paquet de café', articles: 'paquets de café', unArticle: 'un paquet de café' },
];

/** Tout en centimes. Centimes sans zéro final pour éviter « 12,50 » vs « 12,5 ». */
const CENTS_PROPRES = [15, 25, 35, 45, 55, 65, 75, 85, 95];

const tirer2Etapes = () => {
    const n = randInt(2, 4);
    const L = randInt(29, 79) * 100 + randomChoice(CENTS_PROPRES);   // appareil
    const p = randInt(8, 25) * 100 + randomChoice(CENTS_PROPRES);    // article
    const S = n * p;                                                  // total articles
    const T = L + S;                                                  // total payé
    return { n, L, p, S, T, prenom: randomChoice(PRENOMS), ctx: randomChoice(CTX_2ETAPES) };
};

/** Espace insécable entre un nombre et son unité : « 60,25 € » ne doit jamais
 *  se couper en fin de ligne (règle typographique FR, et sur mobile le symbole
 *  partait seul à la ligne suivante — retour utilisateur 2026-08-01). Au bord
 *  d'un trou, le NBSP déclenche en plus le groupe whitespace-nowrap de
 *  TexteTrouEleve. On colle le nombre à SON unité, jamais l'expression
 *  entière : une chaîne insécable trop longue déborderait sur petit écran. */
const NB = ' ';

const enonce2Etapes = ({ n, L, T, prenom, ctx }) =>
    `<p>${prenom} a acheté ${ctx.appareil} et <strong>${n} ${ctx.articles}</strong>. ` +
    `${ctx.def} coûte <strong>${fmtC(L)}${NB}€</strong> ` +
    // « les X sont au même prix » et non « chaque X coûte le même prix » (revue
    // 2026-08-01) : la seule référence de « le même » était l'appareil, la
    // lecture littérale rendait l'énoncé faux. Tournure neutre en genre.
    `et les ${ctx.articles} sont au même prix. ${prenom} a payé en tout <strong>${fmtC(T)}${NB}€</strong>.</p>`;

const gen2EtapesTrouSaisie = () => {
    const t = tirer2Etapes();
    const phrase =
        `L'ensemble des ${t.n} ${t.ctx.articles} coûte ${fmtC(t.T)} − ${fmtC(t.L)} = {{${fmtC(t.S)}}}${NB}€.\n` +
        `${t.ctx.unArticle.charAt(0).toUpperCase() + t.ctx.unArticle.slice(1)} coûte {{${fmtC(t.S)}}} ÷ ${t.n} = {{${fmtC(t.p)}}}${NB}€.`;
    // Consigne allégée (revue 2026-08-01) : la tolérance numérique est active
    // sur ce format (saisie, pas de `strict`), « 12,50 » est déjà accepté —
    // exiger l'écriture sans zéro inutile promettait une sévérité inexistante.
    const texte = enonce2Etapes(t) + '<p>Complète le raisonnement :</p>';
    // Calculatrice AUTORISÉE (revue 2026-08-01) : la compétence est le choix
    // des deux étapes, pas la soustraction de décimaux ni la division posée.
    return avecCalculatrice(makeTexteTrou(phrase, texte, { ...opts(L_2ET), mode: 'saisie' }));
};

const gen2EtapesQcm = () => {
    const t = tirer2Etapes();
    const bonne = fmtC(t.p);
    // Misconceptions : T÷n (oublie de soustraire l'appareil, arrondi aux
    // centimes), T−L (s'arrête à l'étape 1), L÷n (divise la mauvaise donnée).
    const tSurN = Math.round(t.T / t.n);
    const lSurN = Math.round(t.L / t.n);
    // Écart minimal de 50 centimes avec la bonne réponse (revue 2026-08-01) :
    // L/n couvre [7,29..39,98] € et p couvre [8,15..25,95] €, les deux valeurs
    // se croisaient — un choix à 1 centime près (16,75 vs 16,73) départage
    // l'attention, pas le raisonnement. T÷n et S sont toujours à plus de 7 €
    // de p, seul L÷n est filtré (≈ 4 % des tirages) ; les compléments suivent.
    const loin = (v) => Math.abs(v - t.p) >= 50;
    const distracteurs = dedupeChoix(bonne,
        [tSurN, t.S, lSurN].filter(loin).map(fmtC),
        [t.p + 100, t.p + 50, t.p - 50].map(fmtC));
    const texte = enonce2Etapes(t) + `<p>Quel est le prix d'${t.ctx.unArticle} ?</p>`;
    // Unité collée à sa valeur (NBSP) : une étiquette de réponse ne doit jamais
    // se couper entre le nombre et le symbole. La bonne réponse passe par la
    // même fonction que les choix, l'indexOf de makeQcm la retrouve à l'identique.
    const eur = (v) => `${v}${NB}€`;
    // Calculatrice AUTORISÉE (revue 2026-08-01) : même calcul que la saisie à
    // trous, à mener entièrement avant de pouvoir choisir une étiquette.
    return avecCalculatrice(makeQcm([eur(bonne), ...distracteurs.map(eur)], eur(bonne), texte, opts(L_2ET)));
};

const gen2EtapesSaisie = () => {
    const t = tirer2Etapes();
    const texte = enonce2Etapes(t) +
        // Exemple à DEUX décimales (revue 2026-08-01) : la réponse en a toujours
        // deux (CENTS_PROPRES n'a que des centimes non nuls), « 12,5 » montrait
        // une écriture que l'exercice ne produit jamais. 7,25 reste hors des
        // plages du générateur (p ∈ [8,15..25,95] €). Le cadrage « écriture
        // décimale » est conservé : sans lui, « 9 euros 95 » serait refusé.
        `<p>Quel est le prix d'${t.ctx.unArticle} ? Écris ta réponse en écriture décimale (ex. : 7,25).</p>`;
    // Calculatrice AUTORISÉE (revue 2026-08-01) : même calcul que les deux
    // autres formats de la carte, ici avec la valeur exacte à produire.
    return avecCalculatrice(makeSaisie(fmtC(t.p), texte, { ...opts(L_2ET), suffixe: '€' }));
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Course de haies (multi-étapes, donnée inutile)
// ═══════════════════════════════════════════════════════════

/**
 * Donnée inutile injectée dans l'énoncé — EN ROTATION (défaut juge : « la
 * hauteur » systématique devenait un pattern mémorisable). Exactement UNE
 * donnée inutile par tirage → bonne réponse du QCM unique par construction.
 *
 * PHRASE AUTONOME depuis la revue 2026-08-01 : greffée en incise sur la
 * première phrase, elle produisait « Sur un 110 m haies comportant dix haies,
 * la piste compte 8 couloirs » — correct mais bancal. Autonome, elle pourra
 * aussi changer de place dans l'énoncé (fuite à traiter côté QCM).
 */
const DONNEES_INUTILES = [
    { fragment: (v) => `Chaque haie a une hauteur de ${v}${NB}m.`, choix: (v) => `La hauteur des haies (${v}${NB}m)`, valeurs: ['0,838', '0,914', '1,067'] },
    { fragment: (v) => `La piste compte ${v} couloirs.`, choix: (v) => `Le nombre de couloirs (${v})`, valeurs: ['6', '8', '9'] },
    { fragment: (v) => `L'athlète porte le dossard n° ${v}.`, choix: (v) => `Le numéro de dossard (${v})`, valeurs: ['421', '87', '305'] },
];

/** Tout en centimètres : position = d1 + 9e ≤ 10000 < 11000 par bornes. */
const tirerHaies = () => {
    const d1 = randInt(1250, 1450);      // première haie : 12,50 m à 14,50 m
    const e = randInt(850, 950);         // espacement : 8,50 m à 9,50 m
    const pos = d1 + 9 * e;              // dernière haie (10 haies → 9 espacements)
    const arr = 11000 - pos;             // distance à l'arrivée, > 0 par bornes
    const inutile = randomChoice(DONNEES_INUTILES);
    return { d1, e, pos, arr, inutile, vInutile: randomChoice(inutile.valeurs) };
};

/** Centimètres → mètres FR sans zéros finaux ("1372" → "13,72", "8900" → "89"). */
const fmtM = (cm) => {
    const m = Math.floor(cm / 100);
    const c = cm % 100;
    if (c === 0) return String(m);
    const dec = String(c).padStart(2, '0').replace(/0+$/, '');
    return `${m},${dec}`;
};

/**
 * Énoncé de la carte haies.
 *
 * Définition explicite de l'épreuve (revue 2026-08-01) : « le 110 m haies » est
 * un nom d'épreuve, pas une description — or tout le calcul repose sur le fait
 * que la course mesure 110 m, et ce nombre n'apparaissait nulle part ailleurs.
 *
 * `avecDonneeInutile` (revue 2026-08-01) : la donnée inutile n'est INTERROGÉE
 * que par le QCM. Dans le texte à trous, où la ligne de calcul est écrite,
 * l'élève n'a aucune occasion de s'en servir à tort — elle n'y était que du
 * bruit, on la retire.
 */
const enonceHaies = ({ d1, e, inutile, vInutile }, avecDonneeInutile = true) =>
    `<p>Le <strong>110${NB}m haies</strong> est une course de <strong>110${NB}m</strong> ` +
    `pendant laquelle l'athlète franchit <strong>dix haies</strong>. ` +
    (avecDonneeInutile ? `${inutile.fragment(vInutile)} ` : '') +
    `La première haie est à <strong>${fmtM(d1)}${NB}m</strong> de la ligne de départ ` +
    `et les haies sont espacées de <strong>${fmtM(e)}${NB}m</strong>.</p>`;

const genHaiesTrouSaisie = () => {
    const t = tirerHaies();
    // Unité collée à sa valeur (NBSP) : au bord d'un trou, elle déclenche en
    // plus le groupe whitespace-nowrap de TexteTrouEleve.
    // Le NOMBRE D'INTERVALLES est un trou (revue 2026-08-01) : donné en clair,
    // il livrait le seul vrai obstacle du problème (10 haies → 9 intervalles) et
    // ne laissait qu'une exécution — d'autant plus creuse avec la calculatrice.
    // L'attendu 899 porte sur « résoudre des problèmes », donc sur le choix des
    // opérations. Même motif que gen_698, dont les trous portent sur la
    // structure et non sur le résultat.
    const phrase =
        `La dernière haie est à ${fmtM(t.d1)} + {{9}} × ${fmtM(t.e)} = {{${fmtM(t.pos)}}}${NB}m du départ.\n` +
        `La distance de la dernière haie à l'arrivée est 110 − {{${fmtM(t.pos)}}} = {{${fmtM(t.arr)}}}${NB}m.`;
    // Consigne allégée (revue 2026-08-01) : la tolérance numérique est active
    // sur ce format, « 93,80 » est déjà accepté — cf. deux_etapes.
    const texte = enonceHaies(t, false) +
        '<p>Quelle est la distance de la dernière haie à la ligne d\'arrivée ? Complète :</p>';
    // Calculatrice AUTORISÉE (revue 2026-08-01) : la compétence est le repérage
    // des 9 espacements et de la donnée inutile, pas la multiplication posée.
    return avecCalculatrice(makeTexteTrou(phrase, texte, { ...opts(L_HAIES), mode: 'saisie' }));
};

const genHaiesQcm = () => {
    const t = tirerHaies();
    const espacement = `L'espacement entre les haies (${fmtM(t.e)}${NB}m)`;
    const premiere = `La position de la première haie (${fmtM(t.d1)}${NB}m)`;
    const longueur = `La longueur de la course (110${NB}m)`;

    if (randomChoice([true, false])) {
        // Question fermée sur la donnée inutile (Remarque du doc cluster).
        //
        // DEUX CIBLES en rotation (revue 2026-08-01) : avec la seule cible
        // « distance à l'arrivée », les trois distracteurs étaient invariants et
        // la bonne réponse toujours le libellé parasite — mesuré 3019/3019.
        // L'élève cochait « celui qui parle de hauteur, couloirs ou dossard »
        // sans lire l'énoncé. Ici le même libellé est tantôt bonne réponse,
        // tantôt distracteur.
        if (randomChoice([true, false])) {
            // Cible « distance à l'arrivée » : tout sert sauf le parasite.
            const bonne = t.inutile.choix(t.vInutile);
            const texte = enonceHaies(t) +
                '<p>On cherche la distance de la dernière haie à la ligne d\'arrivée. ' +
                'Quelle donnée de l\'énoncé est <strong>inutile</strong> pour répondre ?</p>';
            return avecCalculatrice(makeQcm([bonne, espacement, premiere, longueur], bonne, texte, opts(L_HAIES)));
        }
        // Cible « position de la dernière haie » : elle vaut d1 + 9 × e, la
        // longueur de la course n'y entre pas. Énoncé servi SANS parasite,
        // sinon DEUX données seraient inutiles et la réponse ne serait plus
        // unique. Les trois distracteurs sont alors tous nécessaires au calcul.
        const texte = enonceHaies(t, false) +
            '<p>On cherche la <strong>position de la dernière haie</strong> par rapport au départ. ' +
            'Quelle donnée de l\'énoncé est <strong>inutile</strong> pour répondre ?</p>';
        return avecCalculatrice(makeQcm([longueur, espacement, premiere, 'Le nombre de haies (dix)'],
            longueur, texte, opts(L_HAIES)));
    }
    // Question numérique — distance de la dernière haie à l'arrivée.
    // Unité collée à sa valeur (NBSP), comme partout ailleurs dans le gen.
    const met = (cm) => `${fmtM(cm)}${NB}m`;
    const bonne = met(t.arr);
    // Misconceptions : une seule haie comptée, 10 espacements au lieu de 9,
    // position confondue avec la distance restante.
    const uneHaie = 11000 - (t.d1 + t.e);
    const dixEsp = 11000 - (t.d1 + 10 * t.e);
    const distracteurs = dedupeChoix(bonne,
        [met(uneHaie), met(dixEsp), met(t.pos)],
        [met(t.arr + 100), met(t.arr + 50)]);
    const texte = enonceHaies(t) +
        '<p>Quelle est la distance de la dernière haie à la ligne d\'arrivée ?</p>';
    // Calculatrice AUTORISÉE (revue 2026-08-01) sur toute la carte haies : le
    // même calcul que le texte à trous. Sur les deux questions « donnée
    // inutile », elle est simplement sans emploi — le flag est par format.
    return avecCalculatrice(makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_HAIES)));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Vraisemblance (achat sans poser d'opération)
// ═══════════════════════════════════════════════════════════

const CTX_VRAI = [
    { item: 'stylo', items: 'stylos', autre: 'un cahier', autreNom: 'le cahier' },
    { item: 'gomme', items: 'gommes', autre: 'un classeur', autreNom: 'le classeur' },
    { item: 'croissant', items: 'croissants', autre: 'une brioche', autreNom: 'la brioche' },
    { item: 'carnet', items: 'carnets', autre: 'une trousse', autreNom: 'la trousse' },
];

/**
 * Deux régimes par construction :
 *  - « limite » : n×basP + basC = B ; les deltas sont tirés TOUS du même signe
 *    → le verdict (dépasse / ne dépasse pas) découle du signe, décidable par
 *    « un peu plus / un peu moins » sans poser d'opération.
 *  - « clair »  : n×basP + basC = B ± 1 € au moins ; le verdict découle des
 *    ordres de grandeur, quels que soient les signes des deltas.
 */
const tirerVraisemblance = () => {
    const ctx = randomChoice(CTX_VRAI);
    const prenom = randomChoice(PRENOMS);
    const n = randInt(2, 4);
    const basP = randomChoice([1, 2]);          // € entiers frôlés par l'article
    if (randomChoice([true, false])) {
        // Régime « limite » : basC calé pour que n×basP + basC = B
        const B = randomChoice([5, 10]);
        const basC = B - n * basP;
        if (basC >= 1 && basC <= 4) {
            const depasse = randomChoice([true, false]);
            const signe = depasse ? 1 : -1;
            const p = basP * 100 + signe * randInt(1, 3);
            const c = basC * 100 + signe * randInt(1, 5);
            return { ctx, prenom, n, basP, basC, B, p, c, depasse, regime: 'limite' };
        }
        // basC hors bornes réalistes → repli sur le régime clair ci-dessous
    }
    // Régime « clair » : total entier ≥ B+1 (dépasse) ou ≤ B−1 (passe).
    // B = n×basP + basC ∓ 1 ≥ 2×1 + 2 − 1 = 3 par bornes : toujours valide.
    const depasse = randomChoice([true, false]);
    const basC = randInt(2, 4);
    const B = depasse ? n * basP + basC - 1 : n * basP + basC + 1;
    const p = basP * 100 + randomChoice([-2, -1, 1, 2]);
    const c = basC * 100 + randomChoice([-5, -2, -1, 1, 2, 5]);
    return { ctx, prenom, n, basP, basC, B, p, c, depasse, regime: 'clair' };
};

const enonceVraisemblance = ({ ctx, prenom, n, p, c, B }) =>
    `<p>${prenom} veut acheter <strong>${n} ${ctx.items}</strong> à ${fmtC(p)}${NB}€ pièce ` +
    `et ${ctx.autre} à <strong>${fmtC(c)}${NB}€</strong>. ` +
    `${prenom} a <strong>${B}${NB}€</strong> en poche.</p>`;

const genVraisemblanceQcm = () => {
    const t = tirerVraisemblance();
    const { ctx, n, basP, basC, B, p, c, depasse } = t;
    const plusMoinsP = p > basP * 100 ? 'un peu plus' : 'un peu moins';
    const environC = c > basC * 100 ? `un peu plus de ${basC}${NB}€` : `presque ${basC}${NB}€`;
    const bonne = depasse
        ? `Non, car ${n} × ${fmtC(p)}${NB}€ coûte ${plusMoinsP} de ${n * basP}${NB}€ et ${ctx.autreNom} coûte ${environC} : le total dépasse ${B}${NB}€.`
        : `Oui, car ${n} × ${fmtC(p)}${NB}€ coûte ${plusMoinsP} de ${n * basP}${NB}€ et ${ctx.autreNom} coûte ${environC} : le total ne dépasse pas ${B}${NB}€.`;
    // Distracteurs refondus (revue 2026-08-01). Deux fuites mesurées à 100 % :
    //  - la bonne réponse était toujours la PLUS LONGUE (108 car. contre 70) —
    //    « prends la plus détaillée » suffisait à réussir sans rien calculer ;
    //  - elle était SEULE à porter son verdict — trouver « ça dépasse » donnait
    //    la réponse sans jamais lire la justification, qui est pourtant l'objet
    //    de la question.
    // D'où : longueurs comparables, et un distracteur qui porte le BON verdict
    // avec un raisonnement partiel (il ne regarde qu'un seul des deux achats).
    const mauvaises = [
        // BON verdict, raisonnement partiel — faux dans les deux cas :
        //  · depasse : n × p reste toujours sous B (B ≥ n·basP + 1 par construction) ;
        //  · sinon   : le prix de l'autre article ne suffit pas à conclure.
        depasse
            ? `Non, car ${n} × ${fmtC(p)}${NB}€ dépasse déjà ${B}${NB}€ à lui seul : ${ctx.autreNom} n'y change donc rien.`
            : `Oui, car ${ctx.autreNom} coûte moins de ${basC + 1}${NB}€ : il restera donc de la monnaie, quel que soit le prix des ${ctx.items}.`,
        // Verdict inversé, « justifié » en arrondissant tout dans le mauvais sens
        depasse
            ? `Oui, car chaque ${ctx.item} coûte environ ${basP}${NB}€ et ${ctx.autreNom} environ ${basC}${NB}€ : le total reste sous ${B}${NB}€, il restera de la monnaie.`
            : `Non, car ${n} × ${fmtC(p)}${NB}€ coûte plus de ${n * basP}${NB}€ et ${ctx.autreNom} plus de ${basC}${NB}€ : le total dépasse donc ${B}${NB}€, il manquera de l'argent.`,
        // Oublie de multiplier par n, ou refuse l'ordre de grandeur — en rotation.
        randomChoice([
            `${depasse ? 'Oui' : 'Non'}, car il suffit d'ajouter ${fmtC(p)}${NB}€ et ${fmtC(c)}${NB}€, ce qui ${depasse ? `ne dépasse pas ${B}${NB}€` : `dépasse déjà ${B}${NB}€`} : ${t.prenom} ${depasse ? 'pourra' : 'ne pourra pas'} payer.`,
            `On ne peut pas le savoir sans poser l'opération : des prix comme ${fmtC(p)}${NB}€ et ${fmtC(c)}${NB}€ ne se calculent pas de tête.`,
        ]),
    ];
    // Consigne épurée (revue 2026-08-01) : « Sans poser d'opération » et
    // « Choisis la réponse accompagnée de la justification correcte » retirés.
    const texte = enonceVraisemblance(t) +
        `<p>${t.prenom} pourra-t-${['Alice', 'Inès', 'Maya', 'Sofia'].includes(t.prenom) ? 'elle' : 'il'} réaliser cet achat ?</p>`;
    // PAS de calculatrice (revue 2026-08-01, décision explicite et non un oubli) :
    // l'attendu de cette carte est de trancher par ordre de grandeur. Un total
    // exact supprimerait l'exercice, et rendrait même juste le distracteur
    // « on ne peut pas le savoir sans poser l'opération ».
    return makeQcm([bonne, ...mauvaises], bonne, texte, opts(L_VRAI));
};

const genVraisemblanceVF = () => {
    const t = tirerVraisemblance();
    const { ctx, prenom, n, basP, basC, B, p, c, depasse } = t;
    // Vérités connues PAR CONSTRUCTION (aucun calcul de scoring à la volée) :
    const lignes = [
        `${n} ${ctx.items} coûtent un peu plus de ${n * basP}${NB}€`,
        `${ctx.autreNom.charAt(0).toUpperCase() + ctx.autreNom.slice(1)} coûte moins de ${basC}${NB}€`,
        `Le total des achats dépasse ${B}${NB}€`,
        `Avec ${B}${NB}€, ${prenom} peut payer tous ses achats`,
    ];
    const bonnes = [
        p > basP * 100 ? 0 : 1,   // « un peu plus de n×basP € » ⟺ p > basP
        c < basC * 100 ? 0 : 1,   // « coûte moins de basC € »  ⟺ c < basC
        depasse ? 0 : 1,          // verdict par construction
        depasse ? 1 : 0,          // négation de la ligne 3 → mélange V/F garanti
    ];
    // Pas de consigne (revue 2026-08-01) : le tableau affiche déjà ses deux
    // colonnes « Vrai » et « Faux » — même règle d'épure que la saisie libre et
    // les QCM. « Sans poser d'opération » retiré comme sur le QCM de la carte.
    // PAS de calculatrice non plus : trancher par ordre de grandeur EST l'attendu.
    return makeVraiFaux(lignes, bonnes, enonceVraisemblance(t), opts(L_VRAI));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 (hors quota) — Côté manquant connaissant le périmètre
// ═══════════════════════════════════════════════════════════

/** Tirage borné + repli déterministe (exercice source : 4, 6, 7 → P = 17). */
const tirerTriangle = () => {
    for (let i = 0; i < 40; i++) {
        const ab = randInt(3, 9);
        const cb = randInt(4, 10);
        const ac = randInt(3, 9);
        const distincts = ab !== cb && ab !== ac && cb !== ac;
        const inegalite = ab + cb > ac && ab + ac > cb && cb + ac > ab;
        if (distincts && inegalite) return { ab, cb, ac, p: ab + cb + ac };
    }
    return { ab: 4, cb: 6, ac: 7, p: 17 };
};

/**
 * Figure SVG à l'échelle : BC horizontal, A au-dessus (intersection de cercles).
 * Marges généreuses gauche/droite pour les étiquettes de côtés — et décalage
 * par min(0, xA) : pour un triangle obtus en B, A part à gauche de B, la figure
 * est translatée pour rester entièrement dans le viewBox.
 */
const svgTriangle = ({ ab, cb, ac }) => {
    const s = 28;                                     // px par cm
    const d = cb * s;                                 // BC horizontal
    const x = (d * d + ab * ab * s * s - ac * ac * s * s) / (2 * d);
    const y = Math.sqrt(Math.max(ab * ab * s * s - x * x, 100));
    const margeG = 100, margeD = 100, margeV = 40;
    const offX = margeG + Math.max(0, -x);            // décale si A est à gauche de B
    const h = Math.round(y + 2 * margeV);
    const B = { x: offX, y: h - margeV };
    const C = { x: offX + d, y: h - margeV };
    const A = { x: offX + x, y: h - margeV - y };
    const w = Math.round(Math.max(B.x, C.x, A.x) + margeD);
    const mid = (P, Q) => ({ x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 });
    const mAB = mid(A, B), mAC = mid(A, C), mBC = mid(B, C);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" font-family="sans-serif">` +
        `<polygon points="${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${A.x}" y="${A.y - 10}" text-anchor="middle" font-size="15" font-weight="bold" fill="#1E3A8A">A</text>` +
        `<text x="${B.x - 12}" y="${B.y + 16}" font-size="15" font-weight="bold" fill="#1E3A8A">B</text>` +
        `<text x="${C.x + 4}" y="${C.y + 16}" font-size="15" font-weight="bold" fill="#1E3A8A">C</text>` +
        `<text x="${mAB.x - 10}" y="${mAB.y - 6}" text-anchor="end" font-size="13" fill="#334155">AB = ${ab} cm</text>` +
        `<text x="${mAC.x + 10}" y="${mAC.y - 6}" font-size="13" fill="#DC2626" font-weight="bold">AC = ?</text>` +
        `<text x="${mBC.x}" y="${mBC.y + 18}" text-anchor="middle" font-size="13" fill="#334155">CB = ${cb} cm</text>` +
        '</svg>';
};

// NBSP entre chaque longueur et son unité. Rien à faire dans le SVG : un texte
// SVG ne se replie jamais, la coupure n'existe que dans l'énoncé HTML.
const enonceTriangle = ({ ab, cb, p }) =>
    `<p>Le triangle ABC ci-dessous a un périmètre de <strong>${p}${NB}cm</strong>. ` +
    `On donne AB = ${ab}${NB}cm et CB = ${cb}${NB}cm.</p>`;

// PAS de calculatrice sur cette carte (revue 2026-08-01, décision explicite et
// non un oubli) : le calcul est une soustraction d'entiers à deux chiffres
// (17 − 4 − 6), du calcul mental. La difficulté est de comprendre que le
// périmètre est la somme des trois côtés, pas de mener l'opération.
const genPerimetreSaisie = () => {
    const t = tirerTriangle();
    const texte = enonceTriangle(t) + '<p>Quelle est la longueur du côté AC ?</p>';
    return makeSaisie(String(t.ac), texte, {
        ...opts(L_PERIM), suffixe: 'cm',
        elements_additionnels: imageElement(svgTriangle(t)),
    });
};

const genPerimetreQcm = () => {
    const t = tirerTriangle();
    // Unité collée à sa valeur (NBSP) comme partout ailleurs dans le gen.
    const lg = (v) => `${v}${NB}cm`;
    const bonne = lg(t.ac);
    // Misconceptions : P−AB (oublie un côté), AB+CB (additionne au lieu de
    // soustraire), P−CB (oublie l'autre côté), |AB−CB| (soustrait les deux
    // données entre elles au lieu de les retirer du périmètre), ac−1 (erreur
    // de calcul).
    //
    // Les trois premières sont des SOMMES DE DEUX CÔTÉS : par inégalité
    // triangulaire, chacune dépasse ac. Servies seules, elles faisaient de la
    // bonne réponse la plus petite des quatre dans 8000/8000 tirages — « coche
    // le plus petit nombre » suffisait à réussir (revue 2026-08-01). Deux
    // candidats INFÉRIEURS à ac corrigent cela ; |AB−CB| est toujours < ac
    // (inégalité triangulaire) et ≥ 1 (les côtés sont distincts), ac−1 ≥ 2.
    const distracteurs = dedupeChoix(bonne,
        [t.p - t.ab, t.ab + t.cb, t.p - t.cb, Math.abs(t.ab - t.cb), t.ac - 1].map(lg),
        [lg(t.ac + 1), lg(t.ac + 2)]);
    const texte = enonceTriangle(t) + '<p>Quelle est la longueur du côté AC ?</p>';
    const payload = makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_PERIM));
    payload.elements_additionnels = imageElement(svgTriangle(t));
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    voyages_texte_trou_saisie:     genVoyagesTrouSaisie,
    voyages_texte_trou_drag:       genVoyagesTrouDrag,
    voyages_qcm_unique:            genVoyagesQcm,
    deux_etapes_texte_trou_saisie: gen2EtapesTrouSaisie,
    deux_etapes_qcm_unique:        gen2EtapesQcm,
    deux_etapes_texte:             gen2EtapesSaisie,
    haies_texte_trou_saisie:       genHaiesTrouSaisie,
    haies_qcm_unique:              genHaiesQcm,
    vraisemblance_qcm_unique:      genVraisemblanceQcm,
    vraisemblance_tableau_vf:      genVraisemblanceVF,
    perimetre_texte:               genPerimetreSaisie,
    perimetre_qcm_unique:          genPerimetreQcm,
};

export const CATEGORIES = [
    {
        id: 'voyages', label: L_VOY,
        formatsList: [
            { key: 'voyages_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'voyages_texte_trou_drag',   label: 'Drag' },
            { key: 'voyages_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'deux_etapes', label: L_2ET,
        formatsList: [
            { key: 'deux_etapes_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'deux_etapes_qcm_unique',        label: 'QCM' },
            { key: 'deux_etapes_texte',             label: 'Saisie' },
        ],
    },
    {
        id: 'haies', label: L_HAIES,
        formatsList: [
            { key: 'haies_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'haies_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'vraisemblance', label: L_VRAI,
        formatsList: [
            { key: 'vraisemblance_qcm_unique', label: 'QCM' },
            { key: 'vraisemblance_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'perimetre', label: L_PERIM,
        formatsList: [
            { key: 'perimetre_texte',      label: 'Saisie' },
            { key: 'perimetre_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    fmtC, fmtM, tirerVoyages, tirer2Etapes, tirerHaies,
    tirerVraisemblance, tirerTriangle, svgTriangle, dedupeChoix,
};
